import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Paper,
  Grid,
} from '@mui/material';
import {useGetGenerateView} from '../../../hooks/productions/useProductions';
import {GetGenerateViewRequestParam} from '../../../api/types/production.type';
import '../Demo.scss';

const GenerateViewApiTest: React.FC = () => {
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);
  const [moduletype, setModuletype] = useState<string>('PRODUCTION');
  const [productionId, setProductionId] = useState<number>(216);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const requestParams: GetGenerateViewRequestParam = {
    SecurityGroupId: securityGroupId,
    moduletype: moduletype,
    productionId: productionId,
  };

  const {
    data: generateData,
    isLoading,
    error,
    refetch,
  } = useGetGenerateView(requestParams, enabled);

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
  };

  const handleCopyJson = async () => {
    if (generateData) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(generateData, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const getStatusColor = (className: string) => {
    if (className.includes('success')) return 'success';
    if (className.includes('danger')) return 'error';
    if (className.includes('warning')) return 'warning';
    return 'default';
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Generate View API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Get production generate view with automated checks, status
          information, and generation details.
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Get Generate View
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/view/generateview
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Get production generate view with automated check results,
            generation status, folder paths, and production details.
          </Typography>
        </CardContent>
      </Card>
      {/* Request Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Query Parameters
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label="Security Group ID"
              type="number"
              value={securityGroupId}
              onChange={(e) => setSecurityGroupId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Module Type"
              value={moduletype}
              onChange={(e) => setModuletype(e.target.value)}
              size="small"
              placeholder="e.g., PRODUCTION, EXPORT"
            />
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTestApi}
              data-testid="generate-view-api-test-test-api-button">
              🚀 Test API
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              data-testid="generate-view-api-test-reset-button">
              🔄 Reset
            </Button>
            {enabled && (
              <Button
                variant="outlined"
                onClick={() => refetch()}
                data-testid="generate-view-api-test-refetch-button">
                ♻️ Refetch
              </Button>
            )}
            {generateData && (
              <Button
                variant="outlined"
                onClick={handleCopyJson}
                data-testid="generate-view-api-test-button">
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* Loading State */}
      {isLoading && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">
              ⏳ Loading... Fetching generate view...
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Error State */}
      {error && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {error.message ||
                  'An error occurred while fetching generate view'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Response Data */}
      {generateData && !isLoading && (
        <>
          <Card sx={{marginBottom: 3}}>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Generate View Data
              </Typography>

              {/* Basic Information */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Basic Information
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Module Type Label:</strong>{' '}
                        {generateData.moduleTypeLabel}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production Name:</strong>{' '}
                        {generateData.productionGenerateDetails.productionName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production ID:</strong>{' '}
                        {generateData.productionGenerateDetails.productionId}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Generate Status:</strong>{' '}
                        {
                          generateData.productionGenerateDetails
                            .productionGenerateStatus
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Production State Code:</strong>{' '}
                        <Chip
                          label={
                            generateData.productionGenerateDetails
                              .productionStateCode
                          }
                          size="small"
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Is Mark Complete:</strong>{' '}
                        {generateData.productionGenerateDetails.isMarkComplete
                          ? 'Yes'
                          : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Polling Interval:</strong>{' '}
                        {generateData.productionGeneratePollingInterval}ms
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Automated Checks */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Automated Checks
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Grid container spacing={2}>
                    {[
                      {
                        name: generateData.productionGenerateDetails
                          .automatedCheckStep1Name,
                        status:
                          generateData.productionGenerateDetails
                            .automatedCheckStep1Status,
                        className:
                          generateData.productionGenerateDetails
                            .automatedCheckStep1ClassName,
                      },
                      {
                        name: generateData.productionGenerateDetails
                          .automatedCheckStep2Name,
                        status:
                          generateData.productionGenerateDetails
                            .automatedCheckStep2Status,
                        className:
                          generateData.productionGenerateDetails
                            .automatedCheckStep2ClassName,
                      },
                      {
                        name: generateData.productionGenerateDetails
                          .automatedCheckStep3Name,
                        status:
                          generateData.productionGenerateDetails
                            .automatedCheckStep3Status,
                        className:
                          generateData.productionGenerateDetails
                            .automatedCheckStep3ClassName,
                      },
                      {
                        name: generateData.productionGenerateDetails
                          .automatedCheckStep4Name,
                        status:
                          generateData.productionGenerateDetails
                            .automatedCheckStep4Status,
                        className:
                          generateData.productionGenerateDetails
                            .automatedCheckStep4ClassName,
                      },
                      {
                        name: generateData.productionGenerateDetails
                          .automatedCheckStep5Name,
                        status:
                          generateData.productionGenerateDetails
                            .automatedCheckStep5Status,
                        className:
                          generateData.productionGenerateDetails
                            .automatedCheckStep5ClassName,
                      },
                      {
                        name: generateData.productionGenerateDetails
                          .automatedCheckStep6Name,
                        status:
                          generateData.productionGenerateDetails
                            .automatedCheckStep6Status,
                        className:
                          generateData.productionGenerateDetails
                            .automatedCheckStep6ClassName,
                      },
                      {
                        name: generateData.productionGenerateDetails
                          .automatedCheckStep7Name,
                        status:
                          generateData.productionGenerateDetails
                            .automatedCheckStep7Status,
                        className:
                          generateData.productionGenerateDetails
                            .automatedCheckStep7ClassName,
                      },
                    ].map(
                      (check, index) =>
                        check.name && (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography className="typo-text body3_size">
                                <strong>{check.name}:</strong>
                              </Typography>
                              <Chip
                                label={check.status}
                                color={getStatusColor(check.className)}
                                size="small"
                              />
                            </Box>
                          </Grid>
                        )
                    )}
                  </Grid>
                </Paper>
              </Box>

              {/* Generation Details */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Generation Details
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Generated Bates Range:</strong>{' '}
                        {
                          generateData.productionGenerateDetails
                            .generatedBatesRange
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Total Doc Count:</strong>{' '}
                        {generateData.productionGenerateDetails.totalDocCount}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Completed Doc Count:</strong>{' '}
                        {
                          generateData.productionGenerateDetails
                            .completedDocCount
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Natively Generated Docs:</strong>{' '}
                        {
                          generateData.productionGenerateDetails
                            .nativelyGeneratedDocs
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Can Generate:</strong>{' '}
                        {generateData.productionGenerateDetails.canGenerate
                          ? 'Yes'
                          : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Can Regenerate:</strong>{' '}
                        {generateData.productionGenerateDetails.canRegenerate
                          ? 'Yes'
                          : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Should Continue:</strong>{' '}
                        {generateData.productionGenerateDetails.shouldContinue
                          ? 'Yes'
                          : 'No'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Is Sort By Custom:</strong>{' '}
                        {generateData.productionGenerateDetails.isSortByCustom
                          ? 'Yes'
                          : 'No'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Folder Paths */}
              <Box sx={{marginBottom: 3}}>
                <Typography
                  className="typo-text subhead3_size"
                  sx={{marginBottom: 1}}
                >
                  Folder Paths
                </Typography>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography className="typo-text body3_size">
                        <strong>Temporary Folder Path:</strong>{' '}
                        {
                          generateData.productionGenerateDetails
                            .productionTempararyFolderPath
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography className="typo-text body3_size">
                        <strong>Destination Folder Path:</strong>{' '}
                        {
                          generateData.productionGenerateDetails
                            .productionDestinationFolderPath
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography className="typo-text body3_size">
                        <strong>Production Folder Path:</strong>{' '}
                        {
                          generateData.productionGenerateDetails
                            .productionFolderPath
                        }
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </CardContent>
          </Card>

          {/* JSON Response */}
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 600,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(generateData, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default GenerateViewApiTest;
